import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@store/hooks'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  fetchChartingPreferencesApi,
  saveChartingPreferencesApi,
} from '@services/api/charting-preferences.api'
import { DEFAULT_CHARTING_PREFERENCES_FORM } from '@constants/Settings/chartingPreferences.constants'
import { hydrateClinicChartingPreferencesCache } from '@hooks/useClinicChartingPreferences'
import {
  normalizeDefaultChartingView,
  sanitizeDefaultChartingView,
} from '@utils/default-charting-view.utils'
import { areValuesEqual } from '@utils/settings.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  ChartingYesNo,
  IChartingPreferencesFormState,
  IChartingSystemPreferences,
  ITreatmentPlanPreferences,
} from '@dataTypes/chartingPreferences.type'

const LOADING_CONTAINER = '.settings-charting-preferences'

function readUserIdFromStorage(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { user_id?: string }
    return parsed.user_id ?? ''
  } catch {
    return ''
  }
}

const cloneFormState = (
  state: IChartingPreferencesFormState,
): IChartingPreferencesFormState => ({
  charting_system: { ...state.charting_system },
  treatment_plan: { ...state.treatment_plan },
})

const toYesNo = (value: unknown, fallback: ChartingYesNo): ChartingYesNo => {
  if (value === 'yes' || value === 'no') return value
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return fallback
}

const normalizeFormState = (
  state: IChartingPreferencesFormState,
): IChartingPreferencesFormState => {
  const defaults = DEFAULT_CHARTING_PREFERENCES_FORM
  const cs = state.charting_system
  const tp = state.treatment_plan
  return {
    charting_system: {
      show_tooth_number: toYesNo(
        cs.show_tooth_number,
        defaults.charting_system.show_tooth_number,
      ),
      tooth_numbering_system:
        cs.tooth_numbering_system ??
        defaults.charting_system.tooth_numbering_system,
      show_existing_conditions: toYesNo(
        cs.show_existing_conditions,
        defaults.charting_system.show_existing_conditions,
      ),
      show_missing_teeth: toYesNo(
        cs.show_missing_teeth,
        defaults.charting_system.show_missing_teeth,
      ),
      show_primary_teeth: toYesNo(
        cs.show_primary_teeth,
        defaults.charting_system.show_primary_teeth,
      ),
      show_completed_procedures: toYesNo(
        cs.show_completed_procedures,
        defaults.charting_system.show_completed_procedures,
      ),
      default_charting_view: Array.isArray(cs.default_charting_view)
        ? sanitizeDefaultChartingView(cs.default_charting_view)
        : normalizeDefaultChartingView(cs.default_charting_view),
      autosave_changes: toYesNo(
        cs.autosave_changes,
        defaults.charting_system.autosave_changes,
      ),
    },
    treatment_plan: {
      group_procedures_by_tooth: toYesNo(
        tp.group_procedures_by_tooth,
        defaults.treatment_plan.group_procedures_by_tooth,
      ),
      auto_mark_completed_when_charted: toYesNo(
        tp.auto_mark_completed_when_charted,
        defaults.treatment_plan.auto_mark_completed_when_charted,
      ),
      allow_manual_fee_override: toYesNo(
        tp.allow_manual_fee_override,
        defaults.treatment_plan.allow_manual_fee_override,
      ),
      show_insurance_estimate: toYesNo(
        tp.show_insurance_estimate,
        defaults.treatment_plan.show_insurance_estimate,
      ),
    },
  }
}

const formValuesForCompare = (
  state: IChartingPreferencesFormState,
): string[] => {
  const normalized = normalizeFormState(state)
  const cs = normalized.charting_system
  const tp = normalized.treatment_plan
  return [
    cs.show_tooth_number,
    cs.tooth_numbering_system,
    cs.show_existing_conditions,
    cs.show_missing_teeth,
    cs.show_primary_teeth,
    cs.show_completed_procedures,
    cs.default_charting_view.join(','),
    cs.autosave_changes,
    tp.group_procedures_by_tooth,
    tp.auto_mark_completed_when_charted,
    tp.allow_manual_fee_override,
    tp.show_insurance_estimate,
  ]
}

export interface UseChartingPreferencesReturn {
  clinicId: string
  discardChanges: () => void
  form: IChartingPreferencesFormState
  isDirty: boolean
  loading: boolean
  resetToDefault: () => void
  saveChanges: () => Promise<void>
  setChartingSystemField: <K extends keyof IChartingSystemPreferences>(
    key: K,
    value: IChartingSystemPreferences[K],
  ) => void
  setTreatmentPlanField: <K extends keyof ITreatmentPlanPreferences>(
    key: K,
    value: ITreatmentPlanPreferences[K],
  ) => void
  setHeaderButtons: ReturnType<typeof useSettingsHeader>['setHeaderButtons']
  setHeaderTitle: ReturnType<typeof useSettingsHeader>['setHeaderTitle']
}

const useChartingPreferences = (): UseChartingPreferencesReturn => {
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')
  const userId = useMemo(() => readUserIdFromStorage(), [])
  const [form, setForm] = useState<IChartingPreferencesFormState>(
    cloneFormState(DEFAULT_CHARTING_PREFERENCES_FORM),
  )
  const [savedForm, setSavedForm] = useState<IChartingPreferencesFormState>(
    cloneFormState(DEFAULT_CHARTING_PREFERENCES_FORM),
  )
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const userHasEditedRef = useRef(false)
  const formRef = useRef(form)
  formRef.current = form

  useSettingsSubpageLoading(!dataReady)

  const isDirty = useMemo(
    () =>
      !areValuesEqual(
        formValuesForCompare(form),
        formValuesForCompare(savedForm),
      ),
    [form, savedForm],
  )

  const applyLoadedForm = useCallback((next: IChartingPreferencesFormState) => {
    const normalized = normalizeFormState(next)
    setForm(cloneFormState(normalized))
    setSavedForm(cloneFormState(normalized))
    userHasEditedRef.current = false
  }, [])

  const loadPreferences = useCallback(async () => {
    if (!clinicId) {
      setDataReady(true)
      return
    }
    setLoading(true)
    try {
      const res = await fetchChartingPreferencesApi(clinicId)
      const prefs = res.data?.charting_preferences
      if (prefs?.charting_system && prefs?.treatment_plan) {
        const nextForm = normalizeFormState({
          charting_system: { ...prefs.charting_system },
          treatment_plan: { ...prefs.treatment_plan },
        })
        hydrateClinicChartingPreferencesCache(
          clinicId,
          nextForm.charting_system,
          nextForm.treatment_plan,
        )
        if (userHasEditedRef.current) return
        applyLoadedForm(nextForm)
      }
    } catch (e) {
      console.error(e)
      showToast(
        createFailedToast(
          'Charting preferences',
          'Could not load charting preferences.',
        ),
      )
    } finally {
      setLoading(false)
      setDataReady(true)
    }
  }, [applyLoadedForm, clinicId, showToast])

  useEffect(() => {
    void loadPreferences()
  }, [loadPreferences])

  const setChartingSystemField = useCallback(
    <K extends keyof IChartingSystemPreferences>(
      key: K,
      value: IChartingSystemPreferences[K],
    ): void => {
      userHasEditedRef.current = true
      setForm((prev) => ({
        ...prev,
        charting_system: {
          ...prev.charting_system,
          [key]:
            key === 'default_charting_view'
              ? sanitizeDefaultChartingView(
                  value as IChartingSystemPreferences['default_charting_view'],
                )
              : value,
        },
      }))
    },
    [],
  )

  const setTreatmentPlanField = useCallback(
    <K extends keyof ITreatmentPlanPreferences>(
      key: K,
      value: ITreatmentPlanPreferences[K],
    ): void => {
      userHasEditedRef.current = true
      setForm((prev) => ({
        ...prev,
        treatment_plan: { ...prev.treatment_plan, [key]: value },
      }))
    },
    [],
  )

  const resetToDefault = useCallback(() => {
    userHasEditedRef.current = true
    setForm(cloneFormState(DEFAULT_CHARTING_PREFERENCES_FORM))
  }, [])

  const discardChanges = useCallback(() => {
    userHasEditedRef.current = false
    setForm(cloneFormState(savedForm))
  }, [savedForm])

  const saveChanges = useCallback(async () => {
    if (!clinicId) {
      showToast(
        createFailedToast('Charting preferences', 'No clinic selected.'),
      )
      return
    }
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const normalizedForm = normalizeFormState(formRef.current)
      await saveChartingPreferencesApi({
        clinic_id: clinicId,
        charting_system: normalizedForm.charting_system,
        treatment_plan: normalizedForm.treatment_plan,
        updated_by: userId || undefined,
      })
      hydrateClinicChartingPreferencesCache(
        clinicId,
        normalizedForm.charting_system,
        normalizedForm.treatment_plan,
      )
      const nextSaved = cloneFormState(normalizedForm)
      setSavedForm(nextSaved)
      setForm(nextSaved)
      userHasEditedRef.current = false
      showToast(createSuccessToast('Saved', 'Charting preferences saved.'))
    } catch (e) {
      console.error(e)
      showToast(createFailedToast('Charting preferences', 'Save failed.'))
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, hideLoading, showLoading, showToast, userId])

  return {
    clinicId,
    discardChanges,
    form,
    isDirty,
    loading,
    resetToDefault,
    saveChanges,
    setChartingSystemField,
    setTreatmentPlanField,
    setHeaderButtons,
    setHeaderTitle,
  }
}

export default useChartingPreferences
